import { z } from 'zod';
import { HerkunftFelder } from './auftrag-zusammenfuehren.js';
import { BeauftragtePositionSchema, ProjektBesetzungSchema } from './besetzung.js';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { ProjectSchema } from './project.js';
import { SegmentNoteSchema } from './segment-note.js';

export const StaffingCommentSchema = z
  .object({
    staffing_comment_id: UuidSchema,
    project_id: UuidSchema,
    body: z.string().min(1),
    mentions: z.array(UuidSchema).nullable().optional(),
    // true = Staffing-Kommentar (Staffing-Kontext), false = allgemeiner Projekt-Kommentar.
    is_staffing: z.boolean(),
    created_at: IsoDateTimeSchema,
    created_by: UuidSchema.nullable().optional(),
    // #162: letzte Bearbeitung durch den Autor; NULL = nie bearbeitet.
    edited_at: IsoDateTimeSchema.nullable().optional(),
  })
  .extend(HerkunftFelder);
export type StaffingComment = z.infer<typeof StaffingCommentSchema>;

// --- KFML-Worklist (PV-016) ------------------------------------------------
// Kuratierte Liste der manuell angehakten Projekte (`projects.kfml_worklist`):
// je Projekt das Projektobjekt + der LETZTE Staffing-Kommentar (oder null).
// `ProjectSchema.partial()` wie beim KL-Board: die Route liefert die
// projectWithLeadSelect-Shape, kein vollständiges Project.
// Nicht zu verwechseln mit der KFML-Todoliste (schemas/kfml-todo.ts, LLM-Extraktion).
export const KfmlWorklistItemSchema = z.object({
  project: ProjectSchema.partial().required({ project_id: true }),
  // Fix-Runde 1 (A11): true, solange irgendein Auftrag des Projekts in SF nicht
  // `abgeschlossen` ist (leerer Status zählt als offen) — steuert den Hinweis „in der PV
  // abgeschlossen — SF noch offen" in der Worklist-Karte.
  sf_offen: z.boolean(),
  last_comment: StaffingCommentSchema.nullable(),
});
export type KfmlWorklistItem = z.infer<typeof KfmlWorklistItemSchema>;

export const KfmlWorklistResponseSchema = z.object({
  items: z.array(KfmlWorklistItemSchema),
  // #32: markierte Segment-Notizen (kfml_worklist=true, offen, nicht gelöscht),
  // segmentübergreifend. Eigener Abschnitt in der Worklist-Ansicht.
  notes: z.array(SegmentNoteSchema),
});
export type KfmlWorklistResponse = z.infer<typeof KfmlWorklistResponseSchema>;

// --- Besetzungen (#158) ----------------------------------------------------
export const StaffingBesetzungStatusSchema = z.enum([
  'vorgeschlagen',
  'float',
  'erledigt',
  'ausgeplant',
]);
export type StaffingBesetzungStatus = z.infer<typeof StaffingBesetzungStatusSchema>;

export const StaffingAnzeigeStatusSchema = z.enum([
  'offen',
  'vorgeschlagen',
  'float',
  'erledigt',
  'ausgeplant',
  /** #163: KI-Vorschlag (Position `ki_vorschlag`) — zählt weder als offen noch als erledigt. */
  'zu_bestaetigen',
]);
/** Berechneter Anzeigestatus einer Position. */
export type StaffingAnzeigeStatus = z.infer<typeof StaffingAnzeigeStatusSchema>;

export const StaffingBesetzungSchema = z
  .object({
    staffing_besetzung_id: UuidSchema,
    project_id: UuidSchema,
    beauftragte_position_id: UuidSchema.nullable(),
    user_id: UuidSchema.nullable(),
    /** Freiberufler (Freitext). PII. */
    extern_name: z.string().nullable(),
    sf_projektbesetzung_id: z.string().nullable(),
    status: StaffingBesetzungStatusSchema,
    created_at: IsoDateTimeSchema,
    created_by: UuidSchema.nullable(),
    updated_at: IsoDateTimeSchema,
    updated_by: UuidSchema.nullable(),
  })
  .extend(HerkunftFelder);
export type StaffingBesetzung = z.infer<typeof StaffingBesetzungSchema>;

export const CreateStaffingBesetzungSchema = z.object({
  beauftragte_position_id: UuidSchema.optional(),
  user_id: UuidSchema.optional(),
  extern_name: z.string().trim().min(1).optional(),
  sf_projektbesetzung_id: z.string().trim().min(1).optional(),
  status: StaffingBesetzungStatusSchema.optional(),
});
export type CreateStaffingBesetzung = z.infer<typeof CreateStaffingBesetzungSchema>;

export const PatchStaffingBesetzungSchema = z.object({
  beauftragte_position_id: UuidSchema.nullable().optional(),
  user_id: UuidSchema.optional(),
  extern_name: z.string().trim().min(1).optional(),
  sf_projektbesetzung_id: z.string().trim().min(1).nullable().optional(),
  status: StaffingBesetzungStatusSchema.optional(),
});
export type PatchStaffingBesetzung = z.infer<typeof PatchStaffingBesetzungSchema>;

// --- Staffing-Maske (#158) -------------------------------------------------
export const MaskeBesetzungSchema = StaffingBesetzungSchema.extend({
  person_name: z.string(),
  ist_extern: z.boolean(),
  /** null = nicht verknüpft; sonst: SF-Projektbesetzung existiert noch. */
  sf_aktiv: z.boolean().nullable(),
  person_nicht_in_pv: z.boolean(),
  sf_kontext: z
    .object({
      vk_stundensatz: ProjektBesetzungSchema.shape.vk_stundensatz,
      /** SF Spesenpauschale__c: VK-Nebenkostenpauschale je Tag. */
      vk_nebenkostenpauschale: ProjektBesetzungSchema.shape.vk_nebenkostenpauschale,
      beauftragte_pt: ProjektBesetzungSchema.shape.beauftragte_pt,
      verbleibende_pt: ProjektBesetzungSchema.shape.verbleibende_pt,
      /** R2: Summe vor Ort + remote aus besetzung_ist; null ohne Ist-Zeile. */
      gebucht_stunden: z.number().nullable(),
      letzte_buchung: z.string().nullable(),
    })
    .nullable(),
});
export type MaskeBesetzung = z.infer<typeof MaskeBesetzungSchema>;

// `status` bleibt der Zeilenstatus der Position (ki_vorschlag | bestaetigt); der BERECHNETE Status
// heisst `status_anzeige` (gleicher Schluessel waere ein unerfuellbarer Widerspruch).
// `.extend` statt `.and`: Intersections killen die MCP-Toolliste (zod-intersection-Falle).
export const MaskePositionSchema = BeauftragtePositionSchema.extend({
  status_anzeige: StaffingAnzeigeStatusSchema,
  besetzungen: z.array(MaskeBesetzungSchema),
});
export type MaskePosition = z.infer<typeof MaskePositionSchema>;

export const StaffingMaskeSchema = z.object({
  project_id: UuidSchema,
  positionen: z.array(MaskePositionSchema),
  nicht_zugeordnet: z.object({
    besetzungen: z.array(MaskeBesetzungSchema),
    sf_projektbesetzungen: z.array(
      ProjektBesetzungSchema.extend({
        /** SF-Id (`Projektbesetzung__c`) — mit ihr wird die Zeile einer Besetzung zugeordnet. */
        sf_besetzung_id: z.string(),
        vorschlag_position_id: UuidSchema.nullable(),
      }),
    ),
  }),
  ehemalige: z.array(MaskeBesetzungSchema),
  alles_erledigt: z.boolean(),
  /** #163: Anzahl der KI-Vorschläge (Positionen `ki_vorschlag`), die noch zu bestätigen sind. */
  zu_bestaetigen: z.number().int().nonnegative(),
});
export type StaffingMaske = z.infer<typeof StaffingMaskeSchema>;

// --- KL-Board (Staffing neu, Issue #1) -------------------------------------
// Kuratierte KL-Sicht: je Projekt das Projektobjekt + die Staffing-Maske + die letzten beiden
// Staffing-Kommentare.
export const KlStaffingBoardItemSchema = z.object({
  project: ProjectSchema.partial().required({ project_id: true }),
  /** #158: Positionen und Besetzungen als Baum (ersetzt die frühere Liste `requests`). */
  maske: StaffingMaskeSchema,
  last_comments: z.array(StaffingCommentSchema).max(2),
});
export type KlStaffingBoardItem = z.infer<typeof KlStaffingBoardItemSchema>;

export const KlStaffingBoardResponseSchema = z.object({
  items: z.array(KlStaffingBoardItemSchema),
});
export type KlStaffingBoardResponse = z.infer<typeof KlStaffingBoardResponseSchema>;
